import { useContext, useCallback } from 'react'
import { useMatch } from 'react-router'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { AuthContext } from '@/context/auth-context'
import { BrandEnum } from '@/firebase/auth/auth-jwt-schema'
import { selectActiveMatterId } from '@/matter/store/selectors'
import { selectSidebarItemState } from '@/store/selectors/ui-state.selectors'
import { setSidebarItemOpen, SidebarItemId } from '@/store/slices/ui-state.slice'
import { MattersActions } from '@/matter/store/slice'
import { selectMatterById } from '@/matter/store/selectors'
import { isUnassignedMatter } from '@/matter/utils/is-unassigned-matter'
import { kMyDriveName, kRouteSegments, matterPaths, routePatterns } from '@/routing/paths'
import { AiChat, ArrowLeft, Clipboard, FileClock, Folder, MatterBriefcase, MedChronNew, MedicalBillsNew } from '@/assets/icons'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { canViewChats, canViewMedChron, canViewMedicalBills, canViewTimeline } from '@/matter/utils/matter-permissions'
import type { SideNavItemConfig, SideNavCollapsibleGroupConfig } from '@/components/side-nav/SideNav'

// =============================================================================
// Types
// =============================================================================

type SideNavConfigDashboard = {
  variant: 'dashboard'
  items: SideNavItemConfig[]
  activeItemId: string | undefined
}

type SideNavConfigMatter = {
  variant: 'matter'
  topItems: SideNavItemConfig[]
  collapsibleGroup?: SideNavCollapsibleGroupConfig
  activeItemId: string | undefined
}

export type SideNavConfig = SideNavConfigDashboard | SideNavConfigMatter

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook that builds navigation configuration for the SideNav component.
 *
 * Determines which variant to use based on matter context:
 * - 'dashboard': When no specific matter is selected (activeMatterId is null/"all")
 * - 'matter': When a specific matter is selected (even on non-matter routes like settings)
 *
 * This allows users to maintain their navigation context when visiting settings,
 * account, or other non-matter routes while working within a matter.
 *
 * Handles:
 * - Active item detection via route matching
 * - Brand filtering (HAIKU users don't see Billing Summary)
 * - Anonymous user lock icons
 * - Medical Overview expand/collapse state
 */
export function useSideNavConfig(): SideNavConfig {
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)

  // User state
  const userIsAnon = authContext?.user?.isAnonymous ?? false
  const userBrand = authContext?.userAccountData?.brand ?? BrandEnum.NONE

  // Matter state - determines sidebar variant
  const activeMatterId = useAppSelector(selectActiveMatterId)
  const activeMatter = useAppSelector((state) => selectMatterById(state, { matterId: activeMatterId }))

  // Check if user has an active matter context
  // This drives sidebar variant, independent of current route
  const hasMatterContext = activeMatterId !== null

  // Medical Overview expand/collapse state
  const medicalOverviewState = useAppSelector(selectSidebarItemState(SidebarItemId.MedicalOverview))
  const isMedicalOverviewExpanded = medicalOverviewState?.isOpen ?? false
  const newFilesUxEnabled = kGetFeatureFlagStatus(kFeatureFlags.NEW_FILES_UX)

  // Handler to clear matter context when Home is clicked
  const handleHomeClick = useCallback(() => {
    dispatch(MattersActions.setActiveMatterId({ matterId: null }))
  }, [dispatch])

  // Route matching for active states
  const isMattersRoute = useMatch('/dashboard/matters')
  const isAiAssistantRoute = useMatch(routePatterns.chat.matterFirstFeature)
  const isChronologyRoute = useMatch(routePatterns.chronology.matterFirst)
  const isTimelineRoute = useMatch(routePatterns.timeline.matterFirst)
  const isBillingRoute = useMatch(routePatterns.billingSummaries.matterFirst)
  const isFilesRoute = useMatch(routePatterns.files.matterFirst)
  const isUserFilesRoute = useMatch(routePatterns.userFiles.matterFirst)
  const isMatterOverviewRoute = useMatch({ path: routePatterns.matterOverview, end: true })

  // =============================================================================
  // Dashboard Variant (no specific matter selected)
  // =============================================================================

  if (!hasMatterContext) {
    const items: SideNavItemConfig[] = [
      {
        id: 'matters',
        label: 'Matters',
        href: '/dashboard/matters',
        icon: MatterBriefcase,
        showLock: userIsAnon,
      },
    ]

    // Determine active item
    let activeItemId: string | undefined
    if (isMattersRoute) activeItemId = 'matters'

    return {
      variant: 'dashboard',
      items,
      activeItemId,
    }
  }

  // =============================================================================
  // Matter Variant (specific matter selected)
  // =============================================================================

  // Matter Files path - for unassigned matter, go to drive root; for regular matters, go to matter folder
  const matterFilesHref = isUnassignedMatter(activeMatter)
    ? matterPaths.files.drive(activeMatterId, kMyDriveName)
    : matterPaths.files.folder(activeMatterId, kMyDriveName, activeMatterId)

  // Top items: Home, Matter Overview, AI Assistant, Files (feature-flagged)
  const topItems: SideNavItemConfig[] = [
    {
      id: 'home',
      label: 'Home',
      href: '/dashboard/matters',
      icon: ArrowLeft,
      iconClassName: 'p-[2px]',
      onClick: handleHomeClick,
    },
    {
      id: 'matter-overview',
      label: 'Matter Overview',
      href: matterPaths.overview(activeMatterId),
      icon: MatterBriefcase,
      showLock: userIsAnon,
    },
  ]

  if (canViewChats(activeMatter)) {
    topItems.push({
      id: 'ai-assistant',
      label: 'AI Assistant',
      href: matterPaths.chat.feature(activeMatterId, kRouteSegments.assistant),
      icon: AiChat,
      showLock: userIsAnon,
    })
  }

  // Files: new user-files pipeline, or the legacy files drive
  topItems.push(
    newFilesUxEnabled
      ? { id: 'user-files', label: 'Matter Files', href: matterPaths.userFiles.root(activeMatterId), icon: Folder, showLock: userIsAnon }
      : { id: 'matter-files', label: 'Matter Files', href: matterFilesHref, icon: Folder, showLock: userIsAnon }
  )

  // Collapsible group: "Matter Record" — Timeline (beta) sits at the top, above the medical items.
  // Naming settled in #project-in-app-job-viewers: group "Matter Record", item just "Timeline".
  const matterRecordItems: SideNavItemConfig[] = [
    // Timeline rides the new user-files pipeline, which HAIKU does not have yet — hide the
    // entry for that brand AND behind the pipeline's feature flag (the route guard matches).
    // Owner-only until Timeline gets its own collaborator permission.
    ...(userBrand !== BrandEnum.HAIKU && newFilesUxEnabled && canViewTimeline(activeMatter)
      ? [
          {
            id: 'timeline',
            label: 'Timeline',
            href: matterPaths.timeline.index(activeMatterId),
            icon: FileClock,
            showLock: userIsAnon,
            showBetaTag: true,
          },
        ]
      : []),
    // The living chronology rides the same new user-files pipeline (the hard cutover removed
    // the legacy GCS path), which HAIKU does not have — gate the entry identically to Timeline,
    // matching the route guard (NEW_FILES_UX + BrandAccessGuard excludedBrands=[HAIKU]).
    // Accepted collaborators see it when COLLABORATOR_MEDCHRON_ACCESS is on.
    ...(userBrand !== BrandEnum.HAIKU && newFilesUxEnabled && canViewMedChron(activeMatter)
      ? [
          {
            id: 'chronology',
            label: 'Medical Chronology',
            href: matterPaths.chronology.index(activeMatterId),
            icon: MedChronNew,
          },
        ]
      : []),
  ]

  // Owner-only until Medical Bills gets its own collaborator permission.
  if (userBrand !== BrandEnum.HAIKU && canViewMedicalBills(activeMatter)) {
    matterRecordItems.push({
      id: 'billing-summary',
      label: 'Medical Bills',
      // The feature index is the matter's living billing summary.
      href: matterPaths.billing.index(activeMatterId),
      icon: MedicalBillsNew,
    })
  }

  // Each record item now self-gates on its own permission, so surface the group (and its divider,
  // downstream) whenever at least one item is visible — an empty group renders a bare divider + header.
  const showMatterRecord = matterRecordItems.length > 0

  // Note: id and the expand/collapse redux key intentionally remain 'medical-overview' to preserve
  // each user's persisted localStorage expand state; only the user-facing label/icon changed.
  const collapsibleGroup: SideNavCollapsibleGroupConfig | undefined = showMatterRecord
    ? {
        id: 'medical-overview',
        label: 'Matter Record',
        icon: Clipboard,
        items: matterRecordItems,
        isExpanded: isMedicalOverviewExpanded,
        onExpandToggle: () => dispatch(setSidebarItemOpen(SidebarItemId.MedicalOverview)),
      }
    : undefined

  // Determine active item
  let activeItemId: string | undefined
  if (isMatterOverviewRoute) activeItemId = 'matter-overview'
  else if (isAiAssistantRoute) activeItemId = 'ai-assistant'
  else if (isUserFilesRoute) activeItemId = 'user-files'
  else if (isFilesRoute) activeItemId = 'matter-files'
  else if (isChronologyRoute) activeItemId = 'chronology'
  else if (isTimelineRoute) activeItemId = 'timeline'
  else if (isBillingRoute) activeItemId = 'billing-summary'

  return {
    variant: 'matter',
    topItems,
    collapsibleGroup,
    activeItemId,
  }
}
